import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, ListChecks, ListTodo, Sparkles, MapPin, ShoppingBag } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { calendarItemRoute } from "@/lib/calendarRoute";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtTime, memberColor } from "@/lib/format";

/**
 * DaySheet — Phase 1 unified day view.
 *
 * Opens when the user taps a day heading in CalendarView or asks Kinly
 * "what's on Monday?". Composes events + chores + todos + pinned shopping
 * lists + habit streaks + patterns + "On this day" memories into a single
 * scrollable sheet.
 *
 * Backend: GET /api/families/{family_id}/day/{yyyy-mm-dd}
 *
 * Props:
 *   open: bool
 *   date: "YYYY-MM-DD" (in family timezone)
 *   members: list of person rows (passed in so we don't re-fetch)
 *   onClose: () => void
 */
export function DaySheet({ open, date, members = [], onAddEvent, onClose }) {
  const { family } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !date || !family?.id) return;
    setLoading(true);
    api
      .get(`/families/${family.id}/day/${date}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, date, family?.id]);

  if (!open) return null;

  const niceDate = (() => {
    if (!date) return "";
    try {
      const d = new Date(`${date}T00:00:00`);
      return d.toLocaleDateString(undefined, {
        weekday: "long", day: "numeric", month: "long",
      });
    } catch {
      return date;
    }
  })();

  const events = data?.events || [];
  const chores = data?.chores || [];
  const todos = data?.todos || [];
  const pinned = data?.pinned_lists || [];
  const habits = data?.habits || [];
  const memories = data?.memories || [];
  const total =
    events.length + chores.length + todos.length + pinned.length;

  // Build a Date at 9 AM on the sheet's date so the create form pre-fills
  // somewhere reasonable. The user can edit time inside the create sheet.
  const seedDateAt9 = () => {
    if (!date) return new Date();
    try {
      const d = new Date(`${date}T09:00:00`);
      return isNaN(d.getTime()) ? new Date() : d;
    } catch { return new Date(); }
  };

  const handleAdd = () => {
    if (onAddEvent) {
      onAddEvent(seedDateAt9());
      onClose?.();
    } else {
      // Fallback: navigate to Plan; user opens + manually. Should not happen
      // once every caller wires onAddEvent.
      navigate("/plan");
      onClose?.();
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={niceDate} fullHeight>
      <div data-testid="day-sheet" className="flex flex-col gap-5 pb-4">
        {/* Persistent Add-event CTA — sits ABOVE the list so it's always
            reachable without scrolling, even on an empty day. */}
        <button
          type="button"
          data-testid="day-add-event"
          onClick={handleAdd}
          className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-medium text-[14px] tap inline-flex items-center justify-center gap-2 shadow-sm active:scale-[0.99] transition-transform"
        >
          <Plus className="w-4 h-4" strokeWidth={2.4} />
          Add event for {niceDate.split(",")[0] || "this day"}
        </button>

        {loading && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Loading your day…
          </p>
        )}

        {!loading && total === 0 && (
          <div className="text-center py-6">
            <p className="text-foreground/80 text-sm">Nothing scheduled for {niceDate}.</p>
            <p className="text-muted-foreground text-xs mt-1.5">
              Tap &ldquo;Add event&rdquo; above, ask Kinly, or pin a shopping list to this day.
            </p>
          </div>
        )}

        {events.length > 0 && (
          <Section icon={Calendar} label="Events" testId="day-events">
            {events.map((e) => (
              <button
                key={e.id}
                data-testid={`day-event-${e.id}`}
                onClick={() => navigate(calendarItemRoute(e))}
                className="w-full text-left p-3 rounded-xl bg-secondary/40 hover:bg-secondary/70 tap"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-medium text-[14px] text-foreground truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground shrink-0 tabular-nums">
                    {e.all_day ? "All day" : fmtTime(e.start_at)}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  {e.location_name && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3" /> {e.location_name}
                    </span>
                  )}
                  <ParticipantDots ids={e.participant_person_ids} members={members} />
                </div>
              </button>
            ))}
          </Section>
        )}

        {todos.length > 0 && (
          <Section icon={ListTodo} label="To-dos due" testId="day-todos">
            {todos.map((t) => (
              <li key={t.id} className="p-2.5 rounded-lg bg-secondary/40 text-[13.5px]">
                {t.title}
              </li>
            ))}
          </Section>
        )}

        {chores.length > 0 && (
          <Section icon={ListChecks} label="Chores due" testId="day-chores">
            {chores.map((c) => (
              <li key={c.id} className="p-2.5 rounded-lg bg-secondary/40 text-[13.5px] flex items-center justify-between">
                <span>{c.title}</span>
                {c.reward?.amount ? (
                  <span className="text-xs text-amber-600">{c.reward.amount}★</span>
                ) : null}
              </li>
            ))}
          </Section>
        )}

        {pinned.length > 0 && (
          <Section icon={ShoppingBag} label="Shopping for this day" testId="day-pinned-lists">
            {pinned.map((lst) => (
              <PinnedListCard key={lst.id} list={lst} />
            ))}
          </Section>
        )}

        {habits.length > 0 && (
          <Section icon={Sparkles} label="Streaks worth keeping" testId="day-habits">
            {habits.slice(0, 3).map((h) => {
              const p = members.find((m) => m.id === h.subject_person_id);
              return (
                <li
                  key={h.id}
                  className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-[13px] flex items-center gap-2"
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-background text-[10px] font-semibold"
                    style={{ backgroundColor: memberColor(members, h.subject_person_id) }}
                  >
                    {(p?.display_name || "?").charAt(0)}
                  </div>
                  <span>
                    <strong>{(p?.display_name || "Someone").split(" ")[0]}</strong> · streak{" "}
                    {h.payload?.length_days} days · {prettyHabitLabel(h.payload?.task_signature)}
                  </span>
                </li>
              );
            })}
          </Section>
        )}

        {memories.length > 0 && (
          <Section icon={Sparkles} label="On this day" testId="day-memories">
            <p className="text-xs text-muted-foreground -mt-1">
              {memories.length} {memories.length === 1 ? "moment" : "moments"} from prior years.
            </p>
          </Section>
        )}
      </div>
    </BottomSheet>
  );
}

function Section({ icon: Icon, label, testId, children }) {
  return (
    <section data-testid={testId}>
      <h4 className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </h4>
      <ul className="space-y-1.5 list-none">{children}</ul>
    </section>
  );
}

function ParticipantDots({ ids = [], members = [] }) {
  if (!ids?.length) return null;
  return (
    <div className="flex -space-x-1.5">
      {ids.slice(0, 5).map((id) => {
        const p = members.find((m) => m.id === id);
        return (
          <div
            key={id}
            className="w-4 h-4 rounded-full ring-1 ring-background text-background text-[8px] inline-flex items-center justify-center font-semibold"
            style={{ backgroundColor: memberColor(members, id) }}
          >
            {(p?.display_name || "?").charAt(0)}
          </div>
        );
      })}
    </div>
  );
}

function PinnedListCard({ list }) {
  const items = list.items || [];
  const checklist = items.flatMap((it) => it.checklist || []);
  const total = checklist.length || items.length;
  const done = checklist.filter((c) => c.done).length;
  return (
    <li className="rounded-xl border border-primary/20 bg-card p-3" data-testid={`pinned-list-${list.id}`}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <strong className="text-[14px] text-foreground">{list.name}</strong>
        {total > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums">{done}/{total} picked</span>
        )}
      </div>
      {items.length === 0 && (
        <p className="text-xs text-muted-foreground">Empty list — add items inside To-Dos.</p>
      )}
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.slice(0, 8).map((it) => (
            <li key={it.id} className="text-[13px] text-foreground/85 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-foreground/30 shrink-0"></span>
              <span>{it.title}</span>
            </li>
          ))}
          {items.length > 8 && (
            <li className="text-xs text-muted-foreground italic">…and {items.length - 8} more</li>
          )}
        </ul>
      )}
    </li>
  );
}

function prettyHabitLabel(sig) {
  if (!sig) return "";
  if (sig.startsWith("chore:")) return sig.slice(6).replace(/-/g, " ");
  if (sig.startsWith("recipe:")) return "cooking";
  return sig;
}
